/**
 * Auth Error Boundary
 *
 * Catches errors during authentication initialization and displays a fallback UI.
 * Prevents the entire app from crashing if Web3Auth or SOKA API fails to initialize.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import i18n from '@/i18n';
import { ErrorPage } from '@/shared/components/ErrorPage';
import { reportError } from '@/lib/error-reporter';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Auth initialization error:', error, errorInfo);
    reportError(error, 'AuthErrorBoundary');
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          title={i18n.t('errorPage.title')}
          message={i18n.t('errorPage.message')}
          fixingMessage={i18n.t('errorPage.fixingMessage')}
          reloadLabel={i18n.t('errorPage.reloadButton')}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}
